import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import SmartRtpFormPage from "./SmartRtpFormPage";
import { usePermissions } from "../../../../controllers/PermissionContext";

/**
 * Load edit form by ?editId= (API), with optional ?gameId= bootstrap / resolve.
 * location.state is optional bootstrap only.
 */
export default function SmartRtpEditFormLoader({
  titlePrefix,
  backTo,
  fields,
  fetchById,
  onSave,
  mapInitial = (row) => row,
  extraFields = null,
  defaultValues = {},
  /** When ?gameId= is present and no editId, look up an existing row for that game. */
  resolveByGameId = null,
  /** When ?scope=global and no editId/gameId, look up active global settings. */
  resolveGlobalSetting = null,
  /** Extra defaults applied when opening add form with ?gameId= */
  defaultsForGameId = (gameId) => ({ game_id: gameId }),
  /** Show Instant-Win style Summary cards when a game is selected (Game settings). */
  showGameSummary = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const PERMISSION = usePermissions("smartRtp");
  const [searchParams] = useSearchParams();
  const editIdFromUrl = searchParams.get("editId") || location.state?._id || null;
  const gameIdFromUrl = searchParams.get("gameId") || null;
  const scopeFromUrl = searchParams.get("scope") || null;
  const [editId, setEditId] = useState(editIdFromUrl);
  const [initialValues, setInitialValues] = useState(
    editIdFromUrl ? null : { ...defaultValues, ...(location.state || {}) }
  );
  const [loading, setLoading] = useState(
    !!editIdFromUrl ||
      (!!gameIdFromUrl && !!resolveByGameId) ||
      (scopeFromUrl === "global" && !!resolveGlobalSetting)
  );

  useEffect(() => {
    const allowed =
      PERMISSION?.fullAccess === "Y" ||
      PERMISSION?.list === "Y" ||
      PERMISSION?.view === "Y";
    if (!allowed) {
      navigate("/dashboard");
    }
  }, [PERMISSION, navigate]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (editIdFromUrl) {
        setLoading(true);
        const res = await fetchById(editIdFromUrl);
        if (cancelled) return;
        if (res?.status === true && res.result) {
          setEditId(editIdFromUrl);
          setInitialValues({ ...defaultValues, ...mapInitial(res.result) });
        } else {
          setEditId(editIdFromUrl);
          setInitialValues(null);
        }
        setLoading(false);
        return;
      }

      if (gameIdFromUrl && typeof resolveByGameId === "function") {
        setLoading(true);
        try {
          const found = await resolveByGameId(gameIdFromUrl);
          if (cancelled) return;
          if (found?._id) {
            setEditId(found._id);
            setInitialValues({ ...defaultValues, ...mapInitial(found) });
          } else {
            setEditId(null);
            setInitialValues({
              ...defaultValues,
              ...defaultsForGameId(gameIdFromUrl),
              ...(location.state || {}),
            });
          }
        } catch (error) {
          if (cancelled) return;
          setEditId(null);
          setInitialValues({
            ...defaultValues,
            ...defaultsForGameId(gameIdFromUrl),
            ...(location.state || {}),
          });
        }
        setLoading(false);
        return;
      }

      if (
        scopeFromUrl === "global" &&
        typeof resolveGlobalSetting === "function" &&
        !gameIdFromUrl
      ) {
        setLoading(true);
        try {
          const found = await resolveGlobalSetting();
          if (cancelled) return;
          if (found?._id) {
            setEditId(found._id);
            setInitialValues({ ...defaultValues, ...mapInitial(found) });
          } else {
            setEditId(null);
            setInitialValues({
              ...defaultValues,
              setting_type: "Global",
              ...(location.state || {}),
            });
          }
        } catch (error) {
          if (cancelled) return;
          setEditId(null);
          setInitialValues({
            ...defaultValues,
            setting_type: "Global",
            ...(location.state || {}),
          });
        }
        setLoading(false);
        return;
      }

      setEditId(null);
      setInitialValues({
        ...defaultValues,
        ...(gameIdFromUrl ? defaultsForGameId(gameIdFromUrl) : {}),
        ...(location.state || {}),
      });
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [editIdFromUrl, gameIdFromUrl, scopeFromUrl]);

  if (loading) {
    return (
      <div className="container-fluid p-4 text-center">
        <Spin />
      </div>
    );
  }

  if (editId && !initialValues) {
    return (
      <div className="container-fluid p-4">
        Record not found.
      </div>
    );
  }

  const resolvedFields = typeof fields === "function" ? fields(initialValues) : fields;

  return (
    <SmartRtpFormPage
      title={`${editId ? "Edit" : "Add"} ${titlePrefix}`}
      backTo={backTo}
      initialValues={initialValues || {}}
      fields={extraFields ? [...resolvedFields, ...extraFields] : resolvedFields}
      onSubmit={(values) => onSave(values, editId || initialValues?._id)}
      showGameSummary={showGameSummary}
    />
  );
}
